import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { notificationsApi, type NotificationResponse } from '../api/client';

function formatDateTime(iso: string) {
  try {
    return new Date(iso).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/** EMP-S04 通知一覧（API 連携） */
export default function Notifications() {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const [list, setList] = useState<NotificationResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    setError(null);
    setLoading(true);
    notificationsApi
      .list(token, { limit: 50 })
      .then((res) => setList(res.data ?? []))
      .catch((err) => {
        setError(err instanceof Error ? err.message : t('dashboard.errorLoad'));
        setList([]);
      })
      .finally(() => setLoading(false));
  }, [getToken, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleMarkRead = (n: NotificationResponse) => {
    if (n.is_read) return;
    const token = getToken();
    if (!token) return;
    notificationsApi.markRead(n.id, token).then(() => {
      setList((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true, read_at: new Date().toISOString() } : x)));
    }).catch(() => {});
  };

  return (
    <div className="page notifications-page">
      <h1>{t('employee.notifications')}</h1>

      <p className="notifications-scope-note" role="note">{t('notifications.scopeNote')}</p>

      {error && (
        <div className="notifications-error" role="alert">
          <p>{error}</p>
          <button type="button" className="dashboard-error-retry" onClick={load}>
            {t('dashboard.retry')}
          </button>
        </div>
      )}

      {loading && (
        <div className="notifications-card">
          <p>{t('common.loading')}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="notifications-card notifications-list-wrap">
          {list.length === 0 ? (
            <p className="notifications-empty">{t('notifications.empty')}</p>
          ) : (
            list.map((n) => (
              <div
                key={n.id}
                className={`notifications-item ${n.is_read ? '' : 'notifications-item-unread'}`}
                role="button"
                tabIndex={0}
                onClick={() => handleMarkRead(n)}
                onKeyDown={(e) => e.key === 'Enter' && handleMarkRead(n)}
              >
                <div className="notifications-item-body">
                  <strong>{n.title}</strong>
                  <p className="notifications-item-message">{n.message}</p>
                  <span className="notifications-item-date">{formatDateTime(n.created_at)}</span>
                </div>
                {!n.is_read && <span className="notifications-badge">{t('notifications.unread')}</span>}
              </div>
            ))
          )}
        </div>
      )}

      <p className="settings-back">
        <Link to="/dashboard">{t('common.back')} → {t('settings.backToDashboard')}</Link>
      </p>
    </div>
  );
}
